import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { calculateLevodopaTestFromDocuments } from 'neuro-calculation-commons';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import UPDRSDialog from '../UPDRSDialog/updrsDialog';
import { findLinkedDoc } from 'Routes/Updrs/utils';
import type { TRelevantDocumentType } from 'Routes/Updrs/utils';
import { mapDocumentToNeuroDocument } from 'Utility/documentHandling';

const Levodopa = ({ formData, documents, viewing, setSubDocuments }: IOwnProps): React.JSX.Element => {
  const { document, onChange } = formData;

  // 1. Data preparation
  const beforeDocObj = findLinkedDoc(documents, document.updrsIIIBeforeID);
  const afterDocObj = findLinkedDoc(documents, document.updrsIIIAfterID);

  // 2. Score calculation
  const calculatedScore = React.useMemo(() => {
    // Cast types to ensure compatibility with the shared mapper
    const thisNeuroDoc = mapDocumentToNeuroDocument(document);
    const allNeuroDocs = documents.map(d => mapDocumentToNeuroDocument(d));

    return calculateLevodopaTestFromDocuments(thisNeuroDoc, allNeuroDocs);
  }, [document, documents]);

  // 3. Effects

  // Handle testType defaults
  React.useEffect(() => {
    if (document.testType) return;

    const hasUpdrsLevodopa = documents.some(
      doc => 'type' in doc && doc.type === 'levodopa' && 'testType' in doc && doc.testType === 'UPDRS',
    );

    onChange && onChange({ testType: hasUpdrsLevodopa ? 'UPDRS' : 'MDSUPDRS' });
  }, [document.testType, documents, onChange]);

  // Handle subdocument syncing and cleanup
  React.useEffect(() => {
    if (viewing) return;

    const editingDoc = documents.find(d => d._editing === true);
    const commitsLength = editingDoc?._commitsLength;
    if (!commitsLength) return;

    // Cleanup invalid IDs
    if (document.updrsIIIBeforeID && !beforeDocObj) {
      onChange?.({ updrsIIIBeforeID: undefined });
    }
    if (document.updrsIIIAfterID && !afterDocObj) {
      onChange?.({ updrsIIIAfterID: undefined });
    }

    // Update subdocuments
    const newSubDocs = [];
    if (beforeDocObj?._commitsLength && beforeDocObj._commitsLength >= commitsLength) {
      newSubDocs.push({ name: 'updrs_iii', id: document.updrsIIIBeforeID! });
    }
    if (afterDocObj?._commitsLength && afterDocObj._commitsLength >= commitsLength) {
      newSubDocs.push({ name: 'updrs_iii', id: document.updrsIIIAfterID! });
    }

    setSubDocuments?.(newSubDocs);
  }, [
    viewing,
    documents,
    document.updrsIIIBeforeID,
    document.updrsIIIAfterID,
    beforeDocObj,
    afterDocObj,
    setSubDocuments,
    onChange,
  ]);

  // 4. Render
  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler
            type="PartialDate"
            editing={!viewing}
            name="date"
            formData={formData}
            dateDefault="now"
            isNotCancellable={true}
          />
        </FormRow>
        <FormRow title="updrs.updrsOrMdsupdrs">
          <InputHandler
            type="Radio"
            name="testType"
            editing={!viewing}
            formData={formData}
            options={['UPDRS', 'MDSUPDRS']}
            optionFormatter={(n: string | number) => (
              <FormattedMessage id={`updrs.${n === 'UPDRS' ? 'updrsIII' : 'mds'}`} />
            )}
            disabledOptions={document.updrsIIIBeforeID || document.updrsIIIAfterID ? ['UPDRS', 'MDSUPDRS'] : []}
          />
        </FormRow>
        <FormRow title="updrs.updrsWithoutMedication">
          <UPDRSDialog
            name="updrsIIIBeforeID"
            parentDate={document.date}
            onChange={onChange}
            document={beforeDocObj}
            viewing={viewing}
            context={'levodopa'}
            testType={document.testType ?? 'MDSUPDRS'}
          />
        </FormRow>
        <FormRow title="updrs.updrsWithMedication">
          <UPDRSDialog
            name="updrsIIIAfterID"
            parentDate={document.date}
            onChange={onChange}
            document={afterDocObj}
            viewing={viewing}
            context={'levodopa'}
            retro={beforeDocObj?.retrospectiveData?.[0] === true}
            testType={document.testType ?? 'MDSUPDRS'}
          />
        </FormRow>
      </FormSection>
      <FormSection>
        <FormRow title="updrs.levodopatestScore">
          {typeof calculatedScore === 'number' ? Math.round(calculatedScore * 100) / 100 + ' %' : '-'}
        </FormRow>
        <FormRow title="updrs.levodopatestScoreManual">
          <InputHandler
            type="NumberField"
            editing={!viewing}
            name="manualScore"
            formData={formData}
            width={4.7}
            height={3}
            maxLength={5}
          />
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'levodopatest'>>;
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[];
  viewing: boolean;
  setSubDocuments?: (subDocuments: { name: string; id: string }[]) => void;
}

export default Levodopa;
